import {
  Injectable,
  inject,
  signal,
  computed,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Database, ref, objectVal } from "@angular/fire/database";
import { UtilService } from "./util.service";

/**
 * Rotating ad/info data for the clock. Reactive: the rotation array is a
 * `computed` of two signals — the Firebase config and the requested country —
 * so `init(country)` is just a signal write and the old "data not loaded yet,
 * skipping init" ordering race (init() called before the config arrived) is
 * gone. `rotateArr` is null until the config loads; consumers already guard.
 */
@Injectable({ providedIn: "root" })
export class RotateService {
  private readonly util = inject(UtilService);
  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);

  // Requested country; init() sets it. Defaults to the worldwide rotation.
  private readonly country = signal("world");

  // Rotate config from /rotate — null until loaded.
  private readonly rotateData = toSignal(
    runInInjectionContext(this.injector, () =>
      objectVal<any>(ref(this.mdb, "rotate"))
    ),
    { initialValue: null as any }
  );

  private static readonly FR_COUNTRIES = [
    "France",
    "Guadeloupe",
    "French Guiana",
    "Martinique",
    "French Polynesia",
    "Réunion",
    "New Caledonia",
    "Saint Pierre and Miquelon",
    "Saint-Barthélemy",
  ];

  // The rotation list, derived from config + country. Recomputes automatically
  // when either changes — whichever order they arrive in.
  private readonly _rotateArr = computed(() => {
    const data = this.rotateData();
    if (!data) return null;
    const c = this.country();
    if (RotateService.FR_COUNTRIES.indexOf(c) != -1 && data["fr"]) {
      return this.util.getObjArray(data["fr"]);
    }
    if (data["world"]) return this.util.getObjArray(data["world"]);
    return [];
  });
  // null until the /rotate config node has loaded — callers must guard.
  get rotateArr(): any[] | null {
    return this._rotateArr();
  }

  /** Set the country to rotate for; the list recomputes reactively once the
   *  config is available (no ordering race). */
  init(country: string) {
    if (country) this.country.set(country);
  }
}
